import React, { useState } from "react";
import { trackEvent } from "../../lib/matomo";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Eye, EyeOff, Mail, Lock, AlertCircle } from "lucide-react";
import DataService from "../../components/auth/Service";
import { useUser } from "../../context/UserContext";
import { GoogleLogin } from "@react-oauth/google";
import LogoBlue from "../../assets/icons/peppa_logo_blue.svg";
import { getPostAuthRedirectTarget } from "../../lib/authRedirect";
import ConfirmEmailService from "../sign_up/confirm_email/ConfirmEmailService";

export default function LoginModern({
  embedded = false,
  showGoogleButton = true,
  showLogo = true,
  noCard = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useUser();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [inactiveEmail, setInactiveEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setResendMessage("");

    try {
      const response = await DataService.login(formData);

      if (response.status === 200) {
        try {
          trackEvent("auth", "login_success");
        } catch (_) {}
        try {
          await updateUser?.();
        } catch (_) {}
        navigate(getPostAuthRedirectTarget(location), { replace: true });
      } else {
        const errorMsg = response.data.message || "Erreur de connexion";
        setError(errorMsg);
        try {
          trackEvent("auth", "login_error", errorMsg?.slice(0, 120));
        } catch (_) {}
      }
    } catch (err) {
      let errorMsg = "Une erreur inattendue est survenue. Veuillez réessayer.";

      if (err.response) {
        if (err.response.status >= 500) {
          errorMsg = "Erreur de connexion au serveur, veuillez réessayer plus tard.";
          setInactiveEmail("");
        } else {
          const detail = err.response.data?.detail;
          if (detail === "Inactive user") {
            errorMsg = "Votre compte n'est pas encore activé. Vérifiez vos emails ou demandez un nouvel email de confirmation.";
            setInactiveEmail(formData.email.toLowerCase());
          } else {
            errorMsg = "Email ou mot de passe invalide";
            setInactiveEmail("");
          }
        }
      }

      setError(errorMsg);
      try {
        trackEvent("auth", "login_exception", errorMsg?.slice(0, 120));
      } catch (_) {}
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!inactiveEmail) return;
    setIsResending(true);
    setResendMessage("");
    setError("");
    try {
      const response = await ConfirmEmailService.postResendConfirmEmail(inactiveEmail);
      if (response.status === 200) {
        setResendMessage("Un email de confirmation vous a été renvoyé. Pensez à vérifier vos spams.");
      } else {
        setError("Impossible d'envoyer l'email de confirmation. Réessayez plus tard.");
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        setError("Aucun compte n'a été trouvé avec cet email.");
      } else if (status === 400 && detail === "User mail is already confirmed") {
        setError("Votre email est déjà confirmé. Vous pouvez vous connecter.");
      } else if (status === 429) {
        setError("Un email vient déjà d'être envoyé. Merci d'attendre quelques minutes avant de réessayer.");
      } else {
        setError("Impossible d'envoyer l'email de confirmation. Réessayez plus tard.");
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setError("");
    try {
      const response = await DataService.loginWithGoogle(credentialResponse.credential);
      if (response.status === 200) {
        try {
          trackEvent("auth", "google_login_success");
        } catch (_) {}
        try {
          await updateUser?.();
        } catch (_) {}
        navigate(getPostAuthRedirectTarget(location), { replace: true });
      } else {
        setError("Erreur de connexion Google");
        try {
          trackEvent("auth", "google_login_error");
        } catch (_) {}
      }
    } catch (err) {
      setError("Connexion Google échouée");
      try {
        trackEvent("auth", "google_login_exception");
      } catch (_) {}
    } finally {
      setIsLoading(false);
    }
  };

  const containerClass = embedded
    ? "w-full"
    : "min-h-screen flex items-center justify-center p-4 app-page-gradient";

  const widthClass = embedded ? "w-full max-w-xl mx-auto" : "w-full max-w-md";

  const content = (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {inactiveEmail && !isLoading && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-100">
          <p className="mb-2">
            Votre compte associé à <span className="font-semibold">{inactiveEmail}</span> n'est pas encore activé.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleResendConfirmation}
            disabled={isResending}
          >
            {isResending ? "Renvoi en cours..." : "Renvoyer l'email de confirmation"}
          </Button>
          {resendMessage && (
            <p className="mt-2 text-xs text-green-700 dark:text-green-400 text-center">
              {resendMessage}
            </p>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-foreground">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground opacity-60" />
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="votre@email.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="pl-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-foreground">
            Mot de passe
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-foreground opacity-60" />
            <Input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Votre mot de passe"
              value={formData.password}
              onChange={handleChange}
              required
              className="pl-10 pr-10"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
          <Link
            to={RouterPath.FORGOT_PASSWORD}
            className="text-sm text-primary hover:underline"
          >
            Mot de passe oublié ?
          </Link>
        </div>
        <Button
          type="submit"
          className="w-full"
          disabled={isLoading}
        >
          {isLoading ? "Connexion..." : "Se connecter"}
        </Button>
      </form>

      {showGoogleButton && (
        <div className="mt-2">
          <div className="relative w-full">
            <Button
              type="button"
              variant="outline"
              className="w-full border-border/50 bg-card/90 backdrop-blur-sm hover:bg-accent/50 flex items-center justify-center gap-2 text-foreground"
              disabled={isLoading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-4 w-4">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,16.108,18.961,14,24,14c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657 C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.022C9.505,39.556,16.227,44,24,44z" />
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.236-2.231,4.166-3.994,5.571 c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
              </svg>
              <span>Continuer avec Google</span>
            </Button>
            <div className="google-overlay">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError("Connexion Google échouée")}
                useOneTap={false}
                theme="outline"
                shape="rectangular"
                text="continue_with"
                size="large"
                width="320"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (noCard) {
    return (
      <div className={containerClass}>
        <div className={widthClass}>
          {showLogo && (
            <div className="flex items-center justify-center mb-2">
              <img src={LogoBlue} alt="Peppa" className="h-6" />
            </div>
          )}
          <h2 className="text-xl md:text-2xl font-bold text-foreground text-center mb-4">
            Connexion
          </h2>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={widthClass}>
        <Card className="glass-card border-muted shadow-2xl">
          <CardHeader className="text-center space-y-2">
            {showLogo && (
              <div className="flex items-center justify-center mb-2">
                <img src={LogoBlue} alt="Peppa" className="h-6" />
              </div>
            )}
            <CardTitle className="text-2xl font-bold text-foreground">
              Connexion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">{content}</CardContent>
        </Card>
      </div>
    </div>
  );
}


